import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';
import '../assests/styles.css';
import ScaniaSansBold from '../assests/fonts/ScaniaSans-Bold.woff2';
import ScaniaSansRegular from '../assests/fonts/ScaniaSans-Regular.woff2';
import ScaniaSansHeadlineBold from '../assests/fonts/ScaniaSansHeadline-Bold.woff2';

// Define custom font faces//
const styles = `
  @font-face {
    font-family: 'Scania Sans';
    font-style: normal;
    font-weight: 700;
    src: url(${ScaniaSansBold}) format('woff2');
  }
  @font-face {
    font-family: 'Scania Sans';
    font-style: normal;
    font-weight: 400;
    src: url(${ScaniaSansRegular}) format('woff2');
  }
  @font-face {
    font-family: 'Scania Sans Headline';
    font-style: normal;
    font-weight: 700;
    src: url(${ScaniaSansHeadlineBold}) format('woff2');
  }
`;

// Override page theme font colors
const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#FFFFFF', // Custom font color override
  };
});

const backstageErrorPageOverrides = {
  BackstageErrorPage: {
    styleOverrides: {
      container: {
        alignContent: 'flex-start',
        backgroundColor: '#0B0C0F',
        color: '#EDEFF3',
        minHeight: 'calc(100vh - 120px)',
        padding: '64px 40px',
        '@media (max-width: 999px)': {
          padding: '48px 24px 64px',
        },
        '& .MuiGrid-item': {
          flexBasis: '760px',
          maxWidth: '760px',
        },
        '& a': {
          color: '#9BC9FF',
          fontWeight: 700,
          textDecoration: 'none',
        },
        '& a:hover, & a:focus-visible': {
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
        },
      },
      subtitle: {
        color: '#CDD1DB',
        fontFamily: "'Scania Sans', Arial, sans-serif",
        fontSize: '1rem',
        lineHeight: 1.5,
        marginBottom: '8px',
      },
      title: {
        color: '#EDEFF3',
        fontFamily: "'Scania Sans', Arial, sans-serif",
        letterSpacing: 0,
        paddingBottom: '24px',
        '&.MuiTypography-h2': {
          fontSize: '2rem',
          fontWeight: 700,
          lineHeight: 1.2,
          margin: '14px 0 0',
          paddingBottom: '24px',
        },
        '&.MuiTypography-h6': {
          color: '#EDEFF3',
          fontSize: '1rem',
          lineHeight: 1.5,
          paddingBottom: 0,
        },
      },
    },
  },
  BackstageErrorPageMicDrop: {
    styleOverrides: {
      micDrop: {
        display: 'none',
      },
    },
  },
  BackstageErrorPageStackDetails: {
    styleOverrides: {
      title: {
        paddingBottom: '16px',
      },
    },
  },
};

// Create the dark theme
const darkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      background: {
        default: '#0B0C0F',
        paper: '#15181D',
      },
      primary: {
        main: '#4A89F3',
        light: '#87AFE8',
        dark: '#16417F',
      },
      secondary: {
        main: '#CDD1DB',
        light: '#EDEFF3',
        dark: '#56657A',
      },
      grey: {
        50: '#F9FAFB', // Matching image palette
        100: '#EDEFF3', // Matching image palette
        200: '#E7E9EE', // Matching image palette
        300: '#DBDFE6', // Matching image palette
        400: '#CDD1DB', // Matching image palette
        500: '#B0B7C4', // Matching image palette
        958: '#0D0F13', // Matching image palette
        900: '#1D2229', // Matching image palette
        868: '#242A33', // Matching image palette
        846: '#2B323D', // Matching image palette
        800: '#37414F', // Matching image palette
        700: '#56657A', // Matching image palette
        600: '#868FA2', // Matching image palette
      },
      error: {
        main: '#CF6679', // Muted red for dark theme
        light: '#FF8A9B',
        dark: '#A00000',
      },
      warning: {
        main: '#FFAB00',
        light: '#FFD580',
        dark: '#C67B00',
      },
      success: {
        main: '#4CAF50',
        light: '#80E27E',
        dark: '#087F23',
      },
      info: {
        main: '#64B5F6',
        light: '#9BE7FF',
        dark: '#0288D1',
      },
      navigation: {
        background: '#15181D',
        color: '#EDEFF3',
        indicator: '#4A89F3',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: 'rgba(74, 137, 243, 0.14)',
        },
      },
      text: {
        primary: '#F3F4F7',
        secondary: '#CDD1DB',
      },
    },
  }),
  typography: {
    htmlFontSize: 16,
    fontFamily: "'Scania Sans', Arial, sans-serif",
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      marginBottom: 10,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: 8,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      marginBottom: 6,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 700,
      marginBottom: 6,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 700,
      marginBottom: 4,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 700,
      marginBottom: 2,
      fontFamily: "'Scania Sans', Arial, sans-serif",
    },
  },
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    ...backstageErrorPageOverrides,
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundColor: '#041E42',
          backgroundImage: 'unset', // Remove any default background image
        }),
        title: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '2rem',
          lineHeight: 1.25,
        }, // Set title color to white
        subtitle: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '1rem',
          lineHeight: 1.25,
        }, // Set subtitle color to white
        type: {
          color: '#FFFFFF',
          fontFamily: "'Scania Sans', Arial, sans-serif",
          fontSize: '1rem',
          lineHeight: 1.25,
        }, // Set type color to white
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#FFFFFF', // Set default text color to white for dark mode
        },
      },
    },
  },
});

// Append custom font styles to the head
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default darkTheme;
